import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin, Shield } from "lucide-react";

const SECTIONS = [
  {
    n: 1,
    title: "Acceptance of Terms",
    body: "By accessing, browsing, or using this Website, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy and the Terms of Use. If you do not agree, please do not use the Website. We may modify this Privacy Policy at any time, and such changes will be effective immediately upon posting. Your continued use constitutes acceptance of the updated policy."
  },
  {
    n: 2,
    title: "Intellectual Property",
    body: "All content on this Website—including text, logos, graphics, images, designs, videos, software, and other materials (\"Content\")—is the exclusive property of Ominar.ai or its licensors. Content may not be copied, reproduced, modified, distributed, or used for commercial purposes without prior written consent. Unauthorized use may violate intellectual property laws."
  },
  {
    n: 3,
    title: "Use of Website",
    body: "You agree to use the Website lawfully and not engage in activities such as:",
    list: [
      "Uploading or transmitting viruses, malware, or harmful code.",
      "Attempting unauthorized access to our systems.",
      "Engaging in fraudulent or illegal activities.",
      "Impersonating Ominar.ai or misrepresenting your identity."
    ],
    footer: "We reserve the right to restrict or terminate access for violations."
  },
  {
    n: 4,
    title: "Hyperlinking Policy",
    body: "Our website may contain links to third-party websites. These are provided for convenience only. Ominar.ai does not endorse or take responsibility for the content or privacy practices of third-party websites. Accessing them is at your own risk."
  },
  {
    n: 5,
    title: "Data Collection & Site Visit Information",
    body: "When you visit Ominar.ai, we may automatically collect:",
    list: [
      "Your IP address and server details.",
      "Browser type and device details.",
      "Date, time, and pages visited."
    ],
    footer: "This data is used for analytics and improvement of services."
  },
  {
    n: 6,
    title: "Cookies",
    body: "Ominar.ai may use cookies to enhance your browsing experience. You may choose to disable cookies in your browser, but this may affect functionality."
  },
  {
    n: 7,
    title: "Collection of Personal Information",
    body: "You may browse our site without disclosing personal information. However, if you choose to provide personal details (such as name, email, or contact number) through forms or registrations, we will use it only for the purpose for which it was collected. We do not sell or share personal information with third parties without consent, except when required by law."
  },
  {
    n: 8,
    title: "Disclaimers",
    body: "Our website and its content are provided on an \"as is\" basis. Ominar.ai does not guarantee uninterrupted, error-free, or fully secure access."
  },
  {
    n: 9,
    title: "Limitation of Liability",
    body: "Ominar.ai shall not be liable for any direct, indirect, incidental, or consequential damages arising from your use of the Website, including data loss, business interruption, or system errors."
  },
  {
    n: 10,
    title: "Indemnification",
    body: "You agree to indemnify and hold harmless Ominar.ai, its employees, and affiliates from claims, damages, or losses arising from your use of the Website or violation of this Privacy Policy."
  },
  {
    n: 11,
    title: "Termination",
    body: "We reserve the right to suspend or terminate access at our discretion if users violate these terms."
  },
  {
    n: 12,
    title: "Governing Law",
    body: "This Privacy Policy shall be governed by the laws of India. Any disputes will fall under the jurisdiction of the courts of Pune, Maharashtra, India."
  }
];

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Privacy Policy | Ominar - KidneyCheQ";
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-800">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" data-testid="back-to-home" className="flex items-center gap-3 group">
            <img
              src="https://customer-assets.emergentagent.com/job_1488b547-0470-4710-9082-0fb679cd81f3/artifacts/0ckydwjt_Orange%20kidney%20%281%29.jpg"
              alt="KidneyCheQ"
              className="h-10 w-10 object-contain"
            />
            <span className="font-display text-xl md:text-2xl font-bold text-slate-800 group-hover:text-cyan-600 transition-colors">
              KidneyCheQ
            </span>
          </Link>
          <Link
            to="/"
            data-testid="back-arrow"
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-cyan-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-12 md:pt-20 pb-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/40 via-white to-indigo-50/30" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(6,182,212,0.08),transparent_60%)]" />

        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-100 border border-cyan-200 mb-5">
            <Shield className="w-4 h-4 text-cyan-600" />
            <span className="text-cyan-700 text-sm font-semibold tracking-wide">Legal · Privacy</span>
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-bold text-slate-900 leading-tight mb-4">
            Privacy <span className="gradient-text">Policy</span>
          </h1>
          <p className="text-slate-600 text-base md:text-lg max-w-3xl leading-relaxed">
            By continuing to browse and use our website (https://ominar.ai/), you agree to be bound by the following terms and conditions.
            The term &ldquo;Ominar.ai&rdquo;, &ldquo;Ominar&rdquo;, &ldquo;Ominar Innovations Private Limited&rdquo;, &ldquo;us&rdquo;, or &ldquo;we&rdquo; refers to the owner of the website.
            The term &ldquo;you&rdquo; refers to the user or viewer of our website.
          </p>
        </div>
      </section>

      {/* Sections */}
      <main className="relative pb-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="space-y-6">
            {SECTIONS.map((section) => (
              <article
                key={section.n}
                data-testid={`policy-section-${section.n}`}
                className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 hover:border-slate-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-50 to-indigo-50 border border-cyan-200 flex items-center justify-center font-display font-bold text-cyan-700">
                    {section.n}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-display text-lg md:text-xl font-bold text-slate-900 mb-3">
                      {section.title}
                    </h2>
                    <p className="text-slate-600 leading-relaxed text-sm md:text-base">
                      {section.body}
                    </p>
                    {section.list && (
                      <ul className="mt-4 space-y-2">
                        {section.list.map((item) => (
                          <li key={item} className="flex items-start gap-3 text-slate-600 text-sm md:text-base">
                            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {section.footer && (
                      <p className="mt-4 text-slate-600 leading-relaxed text-sm md:text-base">
                        {section.footer}
                      </p>
                    )}
                  </div>
                </div>
              </article>
            ))}

            {/* 13. Grievance Officer */}
            <article
              data-testid="policy-section-13"
              className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 md:p-8"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white border border-amber-300 flex items-center justify-center font-display font-bold text-amber-700">
                  13
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-display text-lg md:text-xl font-bold text-slate-900 mb-3">
                    Grievance Officer
                  </h2>
                  <p className="text-slate-600 leading-relaxed text-sm md:text-base mb-4">
                    In compliance with the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, the following officer is designated as a Grievance Officer:
                  </p>
                  <div className="bg-white rounded-xl border border-amber-200 p-5 space-y-2">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-xs uppercase tracking-wider text-slate-500 font-mono">Name</span>
                      <span className="text-slate-900 font-semibold">Dr Amar More</span>
                    </div>
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-xs uppercase tracking-wider text-slate-500 font-mono">Designation</span>
                      <span className="text-slate-900 font-semibold">CTO</span>
                    </div>
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="text-xs uppercase tracking-wider text-slate-500 font-mono">Email</span>
                      <a href="mailto:amar.vetpharma@gmail.com" className="text-cyan-600 hover:underline font-medium">
                        amar.vetpharma@gmail.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </article>

            {/* 14. Contact Us */}
            <article
              data-testid="policy-section-14"
              className="bg-gradient-to-br from-cyan-50 to-indigo-50 border border-cyan-200 rounded-2xl p-6 md:p-8"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white border border-cyan-300 flex items-center justify-center font-display font-bold text-cyan-700">
                  14
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-display text-lg md:text-xl font-bold text-slate-900 mb-3">
                    Contact Us
                  </h2>
                  <p className="text-slate-600 leading-relaxed text-sm md:text-base mb-4">
                    If you have any questions about this Privacy Policy, please contact us at:
                  </p>
                  <p className="text-slate-900 font-semibold mb-4">
                    Ominar.ai (Ominar Innovations Pvt Ltd)
                  </p>

                  <div className="space-y-4 text-sm md:text-base">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-1">Registered Address</p>
                        <p className="text-slate-700">
                          C/O, A. T. Salunkhe, Bhai Kotwal Road, Chopda,<br />
                          Dist. Jalgaon. Pin – 425 107. Maharashtra.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-1">Branch Office</p>
                        <p className="text-slate-700">
                          300, NCL Innovation Park, Dr Homi Bhabha Rd,<br />
                          NCL Colony, Pashan, Pune, Maharashtra 411008
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-slate-500 flex-shrink-0" />
                      <a href="mailto:ominarlab@gmail.com" className="text-cyan-600 hover:underline font-medium">
                        ominarlab@gmail.com
                      </a>
                    </div>

                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-slate-500 flex-shrink-0" />
                      <a href="tel:+919075522417" className="text-cyan-600 hover:underline font-medium">
                        +91 90755 22417
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>

          {/* Back to home CTA */}
          <div className="mt-12 text-center">
            <Link
              to="/"
              data-testid="footer-back-home"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to KidneyCheQ
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <p className="text-slate-500 text-sm">
            © 2026 Ominar Innovations Pvt Ltd. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
